import { useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { Linking, Platform, View } from 'react-native'
import RNWebView, { type WebViewNavigation } from 'react-native-webview'
import Loader from './Loader'
import type { UniversalWebViewProps } from './types'

const injectedJSForAppName = 'window.__RN_APP_NAME__ = "nextapp";true;'

export default function UniversalRNWebView<MData>({ ref, ...props }: UniversalWebViewProps<MData>) {
  const {
    source,
    style,
    className,
    startInLoadingState = true,
    injectedJavaScript: injectedJavaScriptProp,
    injectedCSS,
    contentInsetAdjustmentBehavior = 'automatic',
    onLoad,
    onError,
    onMessage,
    onContentReady,
    scrollEnabled = true,
  } = props

  // 当提供 onContentReady 时，使用手动 loading 状态
  const useManualLoading = !!onContentReady
  const [isLoading, setIsLoading] = useState(useManualLoading)

  const webviewRef = useRef<any>(null)

  useImperativeHandle(ref, () => ({
    reload: () => {
      webviewRef.current?.reload()
    },
    postMessage: (message: string) => {
      if (Platform.OS === 'android') {
        // Android's WebView.postMessage dispatches on document (non-bubbling),
        // so it won't reach window.addEventListener listeners in the webview JS.
        webviewRef.current?.injectJavaScript(
          `window.dispatchEvent(new MessageEvent('message',{data:${JSON.stringify(message)}}));true;`
        )
      } else {
        webviewRef.current?.postMessage(message)
      }
    },
    goBack: () => {
      webviewRef.current?.goBack?.()
    },
    goForward: () => {
      webviewRef.current?.goForward?.()
    },
  }))

  const handleShouldStartLoadWithRequest = useCallback((request: WebViewNavigation) => {
    if (request.navigationType === 'click') {
      // Open the URL using the device's default browser
      Linking.openURL(request.url)
      return false
    }
    return true
  }, [])

  const injectedJavaScriptBeforeContentLoaded = useMemo(() => {
    // Lock WebView text size so iOS/Android system font scaling does not affect nextfe pages.
    const baseCSS = 'html{-webkit-text-size-adjust:100%!important;text-size-adjust:100%!important;}'
    const fullCSS = injectedCSS ? `${baseCSS}\n${injectedCSS}` : baseCSS
    const escapedCSS = fullCSS.replace(/\\/g, '\\\\').replace(/'/g, "\\'").replace(/\n/g, '\\n')
    return `
      (function() {
        var css = '${escapedCSS}';
        function injectStyle() {
          var style = document.createElement('style');
          style.textContent = css;
          document.head.appendChild(style);
        }
        if (document.head) injectStyle();
        else document.addEventListener('DOMContentLoaded', injectStyle);
      })();
    `
  }, [injectedCSS])

  const injectedJavaScript = useMemo(
    () => [injectedJavaScriptProp, injectedJSForAppName].filter(Boolean).join('\n'),
    [injectedJavaScriptProp]
  )

  const handleMessage = useCallback(
    (e: { nativeEvent: { data: string } }) => {
      const data = JSON.parse(e.nativeEvent.data)
      // 处理 contentReady 事件
      if (data?.event === 'contentReady') {
        setIsLoading(false)
        onContentReady?.()
        return
      }
      onMessage?.(data)
    },
    [onMessage, onContentReady]
  )

  // 当使用手动 loading 时，不依赖 startInLoadingState
  const effectiveStartInLoadingState = useManualLoading ? false : startInLoadingState

  return (
    <View style={{ flex: 1 }} className={className}>
      <RNWebView
        ref={webviewRef}
        source={source}
        onLoadEnd={onLoad}
        onError={onError}
        onMessage={handleMessage}
        injectedJavaScript={injectedJavaScript}
        injectedJavaScriptBeforeContentLoaded={injectedJavaScriptBeforeContentLoaded}
        applicationNameForUserAgent="nextapp"
        style={style}
        allowsBackForwardNavigationGestures
        startInLoadingState={effectiveStartInLoadingState}
        renderLoading={Loader}
        onShouldStartLoadWithRequest={handleShouldStartLoadWithRequest}
        contentInsetAdjustmentBehavior={contentInsetAdjustmentBehavior}
        scrollEnabled={scrollEnabled}
        webviewDebuggingEnabled={__DEV__}
        {...(Platform.OS === 'android' ? { textZoom: 100 } : null)}
      />
      {useManualLoading && isLoading && <Loader />}
    </View>
  )
}
